/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useEffect, useMemo } from 'react'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { observer } from 'mobx-react-lite'
import { useHistory, useParams } from 'react-router-dom'

import { useMobxStore } from '../../state'
import LoadingSpinner from '../../components/LoadingSpinner'
import { Login, Main } from './steps'
import { toJS } from 'mobx'
import dayjs from 'dayjs'
import sportNinjaLogoDark from './steps/sportninja_dark.svg'
import sportNinjaLogoLight from './steps/sportninja_light.svg'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { Countdown } from 'src/components/Countdown'
import eventNotFound from './event_not_found.svg'
import { Button } from 'src/components/Button'

const RegistrationInvite = observer(({ login, isLogged }) => {
  const history = useHistory()
  const { id } = useParams()
  const {
    registrationInvite: {
      fetchEventInfo,
      state: { eventInfo, isLoading },
    },
  } = useMobxStore()

  const isRegistrationOpen = useMemo(() => {
    const today = dayjs()
    const eventDay = dayjs(eventInfo?.registration?.starts_at)
    const isOpen = today.isAfter(eventDay) || eventInfo?.registration?.is_live

    return isOpen
  }, [eventInfo])

  const apiBackgroundImage = useMemo(() => {
    return eventInfo?.registration?.image?.full_path
  }, [eventInfo?.registration?.image])

  useEffect(() => {
    if (id) {
      fetchEventInfo(id)
    }
  }, [id])

  if (isLoading) {
    return (
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100vh;
        `}
      >
        <LoadingSpinner />
      </div>
    )
  }

  if (!eventInfo?.id || !id || !eventInfo?.registration?.is_registration_on) {
    return (
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          flex-direction: column;
          height: 100vh;
        `}
      >
        <div
          css={css`
            width: 205px;
            height: 205px;
          `}
        >
          <img
            src={eventNotFound}
            css={css`
              width: 100%;
              height: 100%;
            `}
          />
        </div>
        <h1
          css={css`
            color: ${colors.WHITE};
            font-family: Rift;
            font-size: 32px;
            font-style: normal;
            font-weight: 700;
            line-height: 100%; /* 32px */
          `}
        >
          Your request couldn’t be processed
        </h1>
        <p
          css={css`
            color: ${colors.WHITE};
            font-variant-numeric: lining-nums tabular-nums;
            font-size: 18px;
            font-style: normal;
            line-height: 150%; /* 27px */
            text-align: center;
            margin: 16px 0;
          `}
        >
          This competition has not been setup for registration. <br />
          Please notify the League Admin
        </p>
        <div
          css={css`
            width: 200px;
          `}
        >
          <Button
            variant='secondary'
            onClick={() => history.push(ROUTES.HOME)}
            label='back to home'
          />
        </div>
      </div>
    )
  }

  if (!isRegistrationOpen) {
    return (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          background-color: ${eventInfo?.registration?.dark_theme
            ? '#000'
            : '#fff'};
          flex: 2;

          > * {
            color: ${eventInfo?.registration?.dark_theme ? '#fff' : '#000'};
          }
        `}
      >
        <div
          css={css`
            background-image: url(${apiBackgroundImage});
            background-position: center;
            background-size: cover;
            background-repeat: no-repeat;
            width: 100vw;
            height: 360px;
          `}
        />
        <div
          css={css`
            margin-top: -50px;
            display: flex;
            width: 773px;
            height: calc(100vh - 260px);
            padding: 40px;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 24px;
            flex-shrink: 0;
            border-radius: 16px 16px 0px 0px;
            background: #fff;
            box-shadow: 0px 10px 18px -2px rgba(16, 25, 40, 0.07);
          `}
        >
          <h1
            css={css`
              text-align: center;
              font-family: ${isCanlan
                ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                : 'Rift, Arial, Helvetica, sans-serif'};
              font-size: 58px;
              font-style: normal;
              font-weight: 700;
              text-transform: uppercase;
            `}
          >
            {eventInfo?.name}
          </h1>
          <p
            css={css`
              text-align: center;
              font-size: 20px;
              font-style: normal;
              font-weight: 500;
              line-height: normal;
              text-transform: uppercase;
              margin-top: 8px;
            `}
          >
            Registration will open in
          </p>

          <div
            css={css`
              margin-top: 32px;
            `}
          >
            <Countdown
              targetDate={eventInfo?.registration?.starts_at}
              isDarkMode={eventInfo?.registration?.dark_theme}
            />
          </div>

          <div
            css={css`
              margin-top: 32px;
              display: flex;
              align-items: center;
            `}
          >
            <p>Powered by</p>
            <img
              src={
                eventInfo?.registration?.dark_theme
                  ? sportNinjaLogoDark
                  : sportNinjaLogoLight
              }
              css={css`
                margin-left: 8px;
                width: 84px;
                height: 18px;
              `}
            />
          </div>
        </div>
      </div>
    )
  }

  return isLogged ? <Main /> : <Login login={login} />
})

export default RegistrationInvite
